import React, { useState } from 'react';
import {
  ExternalLink,
  Layers,
  Globe,
  FileText,
  CheckCircle2,
  ShieldCheck,
  MapPin,
  Mail,
  Phone,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Star,
  Check,
  ArrowRight,
  Smartphone,
  Laptop,
  HelpCircle,
  Clock,
  Sparkles,
  Award,
} from 'lucide-react';
import { resolveMediaUrl, handleImageError } from '../../../utils/mediaResolver';

// Default storefront hero banners by page slug
const DEFAULT_PAGE_BANNERS = {
  'cookies-policy': '/assets/cookieimg.png',
  'terms-of-use': '/assets/termsofuse img.png',
  'privacy-policy': '/assets/privacypolicy.png',
  'return-refund-policy': '/assets/returnandrefund img.png',
  'terms': '/assets/terms_banner.svg',
  wholesaler: '/assets/wholesale_banner.svg',
  'channel-partner': '/assets/channel-partner.png',
  'contact-us': '/assets/contactUS.png',
  refurbished: '/assets/banner 1.png',
  offers: '/assets/OfferPage.svg',
  'press-release': '/assets/PR1.svg',
  'buy-mobile': '/assets/HeroBanner1.png',
  'buy-laptop': '/assets/LaptopBanner1.png',
  warranty: '/assets/WarrReachUs.svg',
};

// Default Gateway Cards for pages when items are not configured yet
const DEFAULT_GATEWAY_CARDS = {
  buy: [
    {
      eyebrow: 'MOBILES',
      title: 'Buy Mobile',
      description: 'Explore available refurbished mobile devices with certified warranty.',
      image: '/assets/buy-phone.png',
      cta_label: 'Explore mobiles',
    },
    {
      eyebrow: 'LAPTOPS',
      title: 'Buy Laptop',
      description: 'Explore available refurbished laptop devices inspected across 40 parameters.',
      image: '/assets/buy_laptop.png',
      cta_label: 'Explore laptops',
    },
  ],
  sell: [
    {
      eyebrow: 'MOBILE',
      title: 'Sell Mobile',
      description: 'Mobile valuation will be available after the verified selling workflow is connected.',
      status_text: 'Coming Soon',
    },
    {
      eyebrow: 'LAPTOP',
      title: 'Sell Laptop',
      description: 'Laptop valuation will be available after the verified selling workflow is connected.',
      status_text: 'Coming Soon',
    },
    {
      eyebrow: 'HELP',
      title: 'Need help?',
      description: 'Speak with the ZOBOX support team for a verified response.',
      cta_label: 'Open support',
    },
  ],
  partner: [
    {
      eyebrow: 'B2B PLATFORM',
      title: 'Start growing with ZOBIZ',
      description: 'Source refurbished devices in bulk with pan-India delivery.',
      cta_label: 'Explore ZOBIZ',
    },
    {
      eyebrow: 'RETAIL',
      title: 'Become a retail partner',
      description: 'Join the fastest growing refurbished retail network.',
      cta_label: 'Join Retail',
    },
    {
      eyebrow: 'DISTRIBUTION',
      title: 'Channel partner',
      description: 'Partner on regional and bulk procurement distribution.',
      cta_label: 'Partner with Us',
    },
  ],
  minutes: [
    {
      eyebrow: 'NEWS',
      title: 'Latest ZOBOX coverage',
      description: 'Company milestones, sustainability impact and market perspectives.',
      cta_label: 'Read news',
    },
    {
      eyebrow: 'PERSPECTIVES',
      title: 'Latest from ZOBOX',
      description: 'Refurbished tech insights and tips for smart electronic usage.',
      cta_label: 'Read updates',
    },
  ],
};

// All 7 official Legal Policy Pages
const POLICY_PAGES = [
  { slug: 'privacy-policy', label: 'Privacy Policy', path: '/privacyPolicy' },
  { slug: 'terms', label: 'Terms & Conditions', path: '/terms' },
  { slug: 'terms-of-use', label: 'Terms of Use', path: '/termsOfUse' },
  { slug: 'cookies-policy', label: 'Cookies Policy', path: '/cookiesPolicy' },
  { slug: 'shipping-policy', label: 'Shipping & Delivery Policy', path: '/shippingPolicy' },
  { slug: 'return-refund-policy', label: 'Return/Refund Policy', path: '/returnRefundPolicy' },
  { slug: 'warranty-policy', label: 'Warranty Policy', path: '/warrantyPolicy' },
];

function RichContent({ content = '' }) {
  if (!content) return <p className="text-gray-400 italic">No text content added yet.</p>;

  const blocks = String(content).split(/\n{2,}/).filter(Boolean);
  return (
    <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
      {blocks.map((block, idx) => {
        const lines = block.split('\n').filter(Boolean);
        if (lines.length > 0 && lines.every((line) => /^[-*]\s+/.test(line))) {
          return (
            <ul key={idx} className="list-disc pl-5 space-y-1">
              {lines.map((l, i) => (
                <li key={i}>{l.replace(/^[-*]\s+/, '')}</li>
              ))}
            </ul>
          );
        }
        if (/^#{1,3}\s+/.test(block)) {
          return (
            <h3 key={idx} className="text-base font-bold text-gray-900 pt-2">
              {block.replace(/^#{1,3}\s+/, '')}
            </h3>
          );
        }
        return (
          <p key={idx} className="whitespace-pre-line">
            {block}
          </p>
        );
      })}
    </div>
  );
}

export default function PagePreview({ form = {} }) {
  const layout = form.layout || 'content';
  const pageItems = Array.isArray(form.page_items) && form.page_items.length > 0
    ? form.page_items
    : (DEFAULT_GATEWAY_CARDS[form.slug] || []);
  const route = form.route || (form.slug ? `/${form.slug}` : '/content/untitled');
  const status = form.status || 'draft';
  const isLive = status === 'published' || status === 'active';

  // Determine banner image: explicit featured_image -> banner_image -> image -> default page banner
  const bannerImage = form.featured_image || form.banner_image || form.image || DEFAULT_PAGE_BANNERS[form.slug];

  // Check if current page is one of the legal policy pages
  const isPolicy = POLICY_PAGES.some(
    (p) => p.slug === form.slug || String(form.route || '').toLowerCase().includes(p.slug)
  );

  return (
    <div className="bg-white min-h-full flex flex-col font-sans text-gray-900">
      {/* 1. Browser Simulation Bar */}
      <div className="bg-slate-100 border-b px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
        <div className="flex items-center gap-2">
          <Globe className="w-3.5 h-3.5 text-slate-400" />
          <span className="font-mono bg-white px-2 py-0.5 rounded border border-slate-200">
            https://zobox.in{route}
          </span>
          <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-semibold uppercase text-[10px]">
            {layout} layout
          </span>
        </div>
        <div className="flex items-center gap-2">
          {form.show_in_navbar && (
            <span className="px-2 py-0.5 rounded-full font-bold text-[10px] bg-blue-100 text-blue-800 border border-blue-200">
              🧭 In Navbar
            </span>
          )}
          <span
            className={`px-2 py-0.5 rounded-full font-bold text-[10px] uppercase ${
              isLive ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
            }`}
          >
            {isLive ? '● Live / Published' : '○ Draft Preview'}
          </span>
        </div>
      </div>

      {/* 2. Top Banner / Featured Image */}
      {bannerImage ? (
        <div className="w-full bg-slate-50 border-b overflow-hidden flex items-center justify-center p-2 max-h-64">
          <img
            src={resolveMediaUrl(bannerImage)}
            alt={form.title || 'Page header'}
            className="w-full h-auto max-h-60 object-contain rounded-lg"
            onError={handleImageError}
          />
        </div>
      ) : form.slug === 'shipping-policy' ? (
        <div className="bg-[#2B3380] text-white py-8 px-6 text-center shadow-sm">
          <span className="text-[10px] font-bold uppercase tracking-widest text-blue-200 block mb-1">
            {form.eyebrow || 'DELIVERY & COURIER'}
          </span>
          <h1 className="text-2xl md:text-3xl font-bold">
            {form.title || 'Shipping & Delivery Policy'}
          </h1>
          <p className="mt-2 text-xs md:text-sm text-blue-100 max-w-lg mx-auto">
            {form.excerpt || 'Safe, verified, and timely delivery across India'}
          </p>
        </div>
      ) : null}

      {/* 3. Page Layout Rendering */}
      {layout === 'wave' ? (
        /* ================= WAVE PAGE LAYOUT ================= */
        <div className="p-6 md:p-8 space-y-8 flex-1">
          {/* Wave Hero */}
          <div className="border-b pb-6">
            {form.eyebrow && (
              <span className="text-xs font-bold uppercase tracking-wider text-[#2B3380] block mb-1">
                {form.eyebrow}
              </span>
            )}
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">
              {form.title || <span className="text-gray-300 italic">Untitled Wave Page</span>}
            </h1>
            {form.excerpt && (
              <p className="text-gray-600 text-sm mt-2 max-w-2xl leading-relaxed">
                {form.excerpt}
              </p>
            )}
            {(form.cta_text || form.cta_url) && (
              <div className="mt-4">
                <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#2B3380] text-white text-xs font-semibold shadow-sm">
                  {form.cta_text || 'Explore'} →
                </span>
              </div>
            )}
          </div>

          {/* Wave Page Items / Cards Grid */}
          {pageItems.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                <Layers className="w-3.5 h-3.5" />
                <span>Page Items &amp; Cards ({pageItems.length})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pageItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="border border-slate-200 rounded-xl p-4 bg-slate-50/70 hover:bg-slate-50 transition flex flex-col justify-between"
                  >
                    <div>
                      {item.image && (
                        <img
                          src={resolveMediaUrl(item.image)}
                          alt={item.title || ''}
                          className="w-full h-32 object-cover rounded-lg mb-3"
                          onError={handleImageError}
                        />
                      )}
                      {item.eyebrow && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#2B3380] block mb-1">
                          {item.eyebrow}
                        </span>
                      )}
                      <h3 className="font-bold text-gray-900 text-sm">
                        {item.title || <span className="text-gray-300 italic">Item Title</span>}
                      </h3>
                      {item.description && (
                        <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                          {item.description}
                        </p>
                      )}
                    </div>
                    {(item.cta_label || item.status_text || item.status) && (
                      <div className="mt-3 pt-2 border-t flex items-center justify-between">
                        {item.cta_label && (
                          <span className="text-xs font-semibold text-[#2B3380] flex items-center gap-1">
                            {item.cta_label} →
                          </span>
                        )}
                        {(item.status_text || item.status) && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                            {item.status_text || item.status}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Full Rich Content if present */}
          {form.content && (
            <div className="border-t pt-6">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                Full Page Content
              </h3>
              <RichContent content={form.content} />
            </div>
          )}

          {/* About Page Specialized Flow Sections */}
          {(form.slug === 'about' || form.promise_title || form.leader_name) && (
            <div className="border-t pt-8 space-y-8">
              {/* 1. Our Promise */}
              <div className="space-y-3">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#00b4d8]">
                    {form.promise_eyebrow || 'OUR PROMISE'}
                  </span>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mt-1">
                    {form.promise_title || 'Resources should not be wasted.'}
                  </h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { image: '/assets/product.svg', alt: 'Quality products' },
                    { image: '/assets/refurbi.svg', alt: 'Thoughtful refurbishment' },
                    { image: '/assets/tech.svg', alt: 'Useful technology' },
                    { image: '/assets/Ewaste.svg', alt: 'Responsible e-waste management' },
                  ].map((item) => (
                    <div key={item.alt} className="border border-slate-200 rounded-xl p-3 bg-white flex flex-col items-center justify-center text-center">
                      <img src={resolveMediaUrl(item.image)} alt={item.alt} className="w-12 h-12 object-contain mb-2" onError={handleImageError} />
                      <span className="text-[11px] font-medium text-gray-700">{item.alt}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 2. History */}
              <div className="space-y-3">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#00b4d8]">
                    {form.history_eyebrow || 'HISTORY'}
                  </span>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mt-1">
                    {form.history_title || 'The ZOBOX journey'}
                  </h2>
                </div>
                <div className="w-full rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 p-2">
                  <img
                    src={resolveMediaUrl(form.history_image || '/assets/abouHistory.svg')}
                    alt="History of ZOBOX"
                    className="w-full max-h-80 object-contain rounded-xl"
                    onError={handleImageError}
                  />
                </div>
              </div>

              {/* 3. Ecosystem */}
              <div className="space-y-3">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#00b4d8]">
                    {form.ecosystem_eyebrow || 'OUR ECOSYSTEM'}
                  </span>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mt-1">
                    {form.ecosystem_title || 'Connected by better technology'}
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { image: '/assets/Eco1.svg', title: 'Zobox', label: 'E-commerce Store', description: 'Discover a wide selection of top-quality refurbished mobile phones and laptops.', href: '/buy' },
                    { image: '/assets/Eco2.svg', title: 'Zobiz', label: 'Application Store', description: 'A B2B platform offering high-quality refurbished devices at wholesale prices.', href: '/partner' },
                    { image: '/assets/Eco3.svg', title: 'Zomart', label: 'Physical Store', description: 'Physical locations offering reliable refurbished mobile phones and laptops.', href: '/stores' },
                  ].map((item) => (
                    <div key={item.title} className="border border-slate-200 rounded-xl p-4 bg-white flex flex-col justify-between">
                      <div>
                        <img src={resolveMediaUrl(item.image)} alt={item.title} className="w-full h-28 object-cover rounded-lg mb-2" onError={handleImageError} />
                        <span className="text-[9px] font-bold tracking-wider text-[#00b4d8] uppercase block">{item.label}</span>
                        <h3 className="font-bold text-gray-900 text-sm mt-0.5">{item.title}</h3>
                        <p className="text-[11px] text-gray-500 mt-1 line-clamp-3">{item.description}</p>
                      </div>
                      <span className="text-[11px] font-semibold text-[#2b3380] mt-3 block">Explore now →</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 4. Leadership */}
              <div className="rounded-2xl bg-[#f4f6ff] border border-indigo-100 p-6 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                <div className="md:col-span-2 space-y-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#00b4d8]">
                    {form.leader_eyebrow || 'OUR LEADER'}
                  </span>
                  <h2 className="text-2xl font-bold text-[#2b3380]">
                    {form.leader_name || 'Neeraj Chopra'}
                  </h2>
                  <h3 className="text-xs font-semibold text-gray-600">
                    {form.leader_title || 'Founder & CEO, Zobox Protech Limited'}
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed pt-1">
                    {form.leader_bio || "As the founder and CEO of Zobox Protech Limited, I'm driven by a passion for innovation and sustainability."}
                  </p>
                  {form.leader_subtext && (
                    <p className="text-xs text-gray-500 leading-relaxed">
                      {form.leader_subtext}
                    </p>
                  )}
                  <strong className="text-xs text-[#2b3380] block pt-2">
                    {form.leader_quote || '“Resources Should Not Be Wasted”'}
                  </strong>
                </div>
                <div className="flex justify-center">
                  <img
                    src={resolveMediaUrl(form.leader_image || '/assets/NeerajChopra.svg')}
                    alt={form.leader_name || 'Leader'}
                    className="max-h-56 object-contain rounded-xl"
                    onError={handleImageError}
                  />
                </div>
              </div>

              {/* 5. Work with us Banner */}
              {(form.work_image || form.slug === 'about') && (
                <div className="w-full rounded-2xl overflow-hidden border border-slate-200 bg-white p-2">
                  <img
                    src={resolveMediaUrl(form.work_image || '/assets/WorkWithUs.svg')}
                    alt="Work with ZOBOX"
                    className="w-full max-h-64 object-contain"
                    onError={handleImageError}
                  />
                </div>
              )}
            </div>
          )}

          {/* Stores Page Specialized Locations Section */}
          {form.slug === 'stores' && (
            <div className="border-t pt-6 space-y-4">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#2B3380] block">
                ZOBOX STORE NETWORK PREVIEW
              </span>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                {[
                  { city: 'New Delhi', name: 'Karol Bagh Store', address: 'Gaffar Market, Karol Bagh, New Delhi', phone: '+91 88822 10714' },
                  { city: 'Noida', name: 'Sector 63 Store', address: 'H-Block, Sector 63, Noida, UP', phone: '+91 98737 88964' },
                  { city: 'Pratapgarh', name: 'Main City Store', address: 'Civil Lines, Pratapgarh, UP', phone: '+91 98737 88964' },
                ].map((s) => (
                  <div key={s.name} className="border rounded-xl p-3 bg-white space-y-1.5 shadow-sm">
                    <span className="text-[9px] font-bold uppercase text-indigo-700 block">{s.city}</span>
                    <strong className="text-slate-900 block">{s.name}</strong>
                    <div className="flex items-start gap-1 text-slate-500 text-[11px]">
                      <MapPin className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>{s.address}</span>
                    </div>
                    <div className="flex items-center gap-1 text-slate-500 text-[11px] pt-1">
                      <Phone className="w-3 h-3 text-emerald-600" />
                      <span>{s.phone}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : layout === 'gateway' ? (
        /* ================= GATEWAY PAGE LAYOUT ================= */
        <div className="p-6 md:p-8 space-y-6 flex-1">
          <div className="bg-gradient-to-r from-[#1e2669] to-[#2B3380] text-white p-6 rounded-2xl shadow-sm">
            {form.eyebrow && (
              <span className="text-xs font-bold uppercase tracking-wider text-blue-200 block mb-1">
                {form.eyebrow}
              </span>
            )}
            <h1 className="text-2xl md:text-3xl font-extrabold">
              {form.title || <span className="text-white/40 italic">Gateway Page Title</span>}
            </h1>
            {form.excerpt && (
              <p className="text-blue-100 text-sm mt-2 max-w-xl leading-relaxed">
                {form.excerpt}
              </p>
            )}
          </div>

          {pageItems.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pageItems.map((item, idx) => (
                <div key={idx} className="border rounded-xl p-4 bg-white shadow-sm flex flex-col justify-between">
                  <div>
                    {item.image && (
                      <img
                        src={resolveMediaUrl(item.image)}
                        alt={item.title || ''}
                        className="w-full h-36 object-contain p-2 bg-slate-50 rounded-lg mb-3"
                        onError={handleImageError}
                      />
                    )}
                    {item.eyebrow && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">
                        {item.eyebrow}
                      </span>
                    )}
                    <h3 className="font-bold text-gray-900 text-base mt-0.5">{item.title}</h3>
                    <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                  </div>
                  {(item.cta_label || item.status_text || item.status) && (
                    <div className="mt-4 pt-3 border-t flex items-center justify-between">
                      {item.cta_label && (
                        <span className="text-xs font-bold text-[#2B3380] flex items-center gap-1">
                          {item.cta_label} →
                        </span>
                      )}
                      {(item.status_text || item.status) && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                          {item.status_text || item.status}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : isPolicy ? (
        /* ================= LEGAL POLICY LAYOUT ================= */
        <div className="p-6 md:p-8 flex-1 max-w-5xl mx-auto w-full flex flex-col md:flex-row gap-6">
          {/* Policy Navigation Sidebar */}
          <div className="w-full md:w-64 flex-shrink-0">
            <div className="bg-slate-50 border rounded-2xl p-4 space-y-1 text-xs">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2 px-2">
                Policy Directory
              </span>
              {POLICY_PAGES.map((p) => {
                const isActive = p.slug === form.slug || form.route?.includes(p.slug);
                return (
                  <div
                    key={p.slug}
                    className={`px-3 py-2 rounded-lg font-medium transition ${
                      isActive
                        ? 'bg-[#2B3380] text-white shadow-sm'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {p.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Policy Main Content */}
          <div className="flex-1 space-y-6">
            <div>
              {form.eyebrow && (
                <span className="text-xs font-bold uppercase tracking-wider text-[#2B3380] block mb-1">
                  {form.eyebrow}
                </span>
              )}
              <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                {form.title || 'Legal Policy'}
              </h1>
              {form.excerpt && (
                <p className="text-gray-600 text-xs md:text-sm mt-2 leading-relaxed border-l-2 border-[#2B3380] pl-3 italic">
                  {form.excerpt}
                </p>
              )}
            </div>

            <div className="border-t pt-4">
              <RichContent content={form.policy_content || form.content || `# 1. Introduction & Overview\nThis policy outlines the principles, responsibilities, and guidelines governing the use of ZOBOX services and refurbished products.\n\n# 2. Scope & Applicability\nAll users, customers, and partners agree to adhere to verified standards of transaction and dispute management.\n\n# 3. Contact & Redressal\nFor queries regarding this policy, reach out to **legal@zobox.in** or call **+91 9873788964**.`} />
            </div>
          </div>
        </div>
      ) : (
        /* ================= CONTENT & CUSTOM PAGE LAYOUT ================= */
        <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-6 flex-1 w-full">
          <div>
            {form.eyebrow && (
              <span className="text-xs font-bold uppercase tracking-wider text-[#2B3380] block mb-1">
                {form.eyebrow}
              </span>
            )}
            <h1 className="text-2xl md:text-4xl font-extrabold text-gray-900">
              {form.title || <span className="text-gray-300 italic">Page Title</span>}
            </h1>
            {form.excerpt && (
              <p className="text-gray-600 text-base mt-3 leading-relaxed border-l-2 border-[#2B3380] pl-4 italic">
                {form.excerpt}
              </p>
            )}
          </div>

          {/* Full Rich / Policy Content */}
          {(form.policy_content || form.content) && (
            <div className="border-t pt-4">
              <RichContent content={form.policy_content || form.content} />
            </div>
          )}

          {/* Specialized Section: Wholesaler Partner Steps */}
          {form.slug === 'wholesaler' && (
            <div className="border-t pt-6 space-y-6">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#00b4d8]">
                  HOW TO BECOME A PARTNER
                </span>
                <h2 className="text-xl font-bold text-gray-900 mt-1">
                  Three simple steps to start growing
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { image: '/assets/app.svg', step: 'Step 1', title: 'Download Zobiz App', desc: 'Available on Google Play & App Store' },
                  { image: '/assets/register.svg', step: 'Step 2', title: 'Register & KYC', desc: 'Instant verification with GST or Aadhaar' },
                  { image: '/assets/grow.svg', step: 'Step 3', title: 'Start Growing', desc: 'Order at wholesale prices with free delivery' },
                ].map((s) => (
                  <div key={s.step} className="border rounded-xl p-4 bg-slate-50 flex flex-col items-center text-center">
                    <img src={resolveMediaUrl(s.image)} alt={s.title} className="w-16 h-16 object-contain mb-2" onError={handleImageError} />
                    <span className="text-[10px] font-bold uppercase text-[#2B3380]">{s.step}</span>
                    <strong className="text-xs text-gray-900 mt-0.5">{s.title}</strong>
                    <p className="text-[11px] text-gray-500 mt-1">{s.desc}</p>
                  </div>
                ))}
              </div>
              <div className="border rounded-2xl overflow-hidden p-2 bg-white shadow-sm flex items-center justify-center">
                <img src={resolveMediaUrl('/assets/Zobiz.svg')} alt="Zobiz Platform" className="max-h-60 object-contain" onError={handleImageError} />
              </div>
            </div>
          )}

          {/* Specialized Section: Refurbished Quality Certification */}
          {form.slug === 'refurbished' && (
            <div className="border-t pt-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div className="space-y-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#00b4d8]">
                    WHAT DOES REFURBISHED MEAN?
                  </span>
                  <h2 className="text-xl font-bold text-gray-900">
                    Inspected, Tested, and Certified Like New
                  </h2>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Refurbished products are pre-owned items that undergo comprehensive functional testing, cosmetic classification, component replacement, and deep cleaning before being certified for resale.
                  </p>
                </div>
                <div className="flex justify-center">
                  <img src={resolveMediaUrl('/assets/refurbished.png')} alt="Refurbished Lifecycle" className="max-h-48 object-contain" onError={handleImageError} />
                </div>
              </div>
              <div className="border rounded-2xl p-4 bg-indigo-50/50 flex items-center gap-4">
                <img src={resolveMediaUrl('/assets/refurbiCertified.png')} alt="40-Step Certified" className="w-16 h-16 object-contain flex-shrink-0" onError={handleImageError} />
                <div className="text-xs">
                  <strong className="text-gray-900 block font-bold">40-Parameter Certified Quality Guarantee</strong>
                  <p className="text-gray-600 mt-0.5">Every device includes minimum 6 to 12-month doorstep warranty and rigorous hardware verification.</p>
                </div>
              </div>
            </div>
          )}

          {/* Specialized Section: Press Releases */}
          {form.slug === 'press-release' && (
            <div className="border-t pt-6 space-y-4">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#00b4d8]">
                MEDIA COVERAGE &amp; ANNOUNCEMENTS
              </span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { image: '/assets/PR1.svg', title: 'ZOBOX Expands Certified Refurbished Smartphone Network', date: 'July 2024' },
                  { image: '/assets/PR2.svg', title: 'Festive Deals & Technology Access Across Tier-2/3 Cities', date: 'July 2024' },
                  { image: '/assets/PR3.svg', title: 'Sustainable E-Waste Management Partnerships Announced', date: 'August 2024' },
                  { image: '/assets/PR4.svg', title: 'ZOBOX Retail Network Reaches 50+ City Milestone', date: 'September 2024' },
                ].map((pr) => (
                  <div key={pr.title} className="border rounded-xl p-3 bg-white shadow-sm flex gap-3 items-center">
                    <img src={resolveMediaUrl(pr.image)} alt="" className="w-14 h-14 object-cover rounded-lg flex-shrink-0" onError={handleImageError} />
                    <div className="text-xs">
                      <span className="text-[10px] font-bold text-slate-400 block">{pr.date}</span>
                      <strong className="text-slate-800 leading-snug block mt-0.5">{pr.title}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Specialized Section: Help Center */}
          {form.slug === 'help' && (
            <div className="border-t pt-6 space-y-4">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#00b4d8]">
                SUPPORT CHANNELS
              </span>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { image: '/assets/contact-email.png', title: 'Email Support', detail: 'support@zobox.in' },
                  { image: '/assets/contact-twitter.png', title: 'Social Desk', detail: '@ZoboxSupport' },
                  { image: '/assets/liveChat.png', title: 'Live Chat', detail: 'Mon-Sat 10:30am - 6:30pm' },
                ].map((c) => (
                  <div key={c.title} className="border rounded-xl p-4 bg-white text-center flex flex-col items-center shadow-sm">
                    <img src={resolveMediaUrl(c.image)} alt={c.title} className="w-10 h-10 object-contain mb-2" onError={handleImageError} />
                    <strong className="text-xs text-gray-900">{c.title}</strong>
                    <span className="text-[11px] text-gray-500 mt-1">{c.detail}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Specialized Section: Grade & Condition Standards */}
          {form.slug === 'grade-condition' && (
            <div className="border-t pt-6 space-y-4">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#00b4d8]">
                DEVICE CONDITION TIERS
              </span>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { tier: 'Excellent', status: 'Like New', desc: 'Flawless display, zero visible scratches, 100% battery health', img: '/assets/display.svg' },
                  { tier: 'Superb', status: 'Very Good', desc: 'Minor micro-scratches barely visible, completely tested components', img: '/assets/display1.svg' },
                  { tier: 'Good', status: 'Good Value', desc: 'Light cosmetic wear, guaranteed functional performance & warranty', img: '/assets/display3.svg' },
                ].map((g) => (
                  <div key={g.tier} className="border rounded-xl p-4 bg-white shadow-sm space-y-2">
                    <div className="flex items-center justify-between">
                      <strong className="text-sm font-bold text-[#2B3380]">{g.tier}</strong>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                        {g.status}
                      </span>
                    </div>
                    <img src={resolveMediaUrl(g.img)} alt={g.tier} className="w-full h-20 object-contain py-1" onError={handleImageError} />
                    <p className="text-[11px] text-gray-500 leading-relaxed">{g.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Specialized Section: Offers & Deals */}
          {form.slug === 'offers' && (
            <div className="border-t pt-6 space-y-4">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#00b4d8]">
                FEATURED PROMOTIONS
              </span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1, 2].map((num) => (
                  <div key={num} className="border rounded-xl p-4 bg-white shadow-sm space-y-2">
                    <img src={resolveMediaUrl('/assets/OfferPage.svg')} alt="Offer" className="w-full h-32 object-contain bg-slate-50 rounded-lg p-2" onError={handleImageError} />
                    <strong className="text-xs text-gray-900 block">Extended 12-Month Brand Warranty Free</strong>
                    <p className="text-[11px] text-gray-500">Claim free extended warranty on verified smartphone models this month.</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Specialized Section: Channel & Partner Programs */}
          {['become-partner', 'zobiz-partner', 'channel-partner'].includes(form.slug) && (
            <div className="border-t pt-6 space-y-4">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#00b4d8]">
                PARTNER PLATFORM ECOSYSTEM
              </span>
              <div className="border rounded-2xl p-6 bg-[#f8f9ff] flex flex-col md:flex-row items-center gap-6">
                <div className="flex-1 space-y-2 text-xs">
                  <h3 className="text-base font-bold text-[#2B3380]">Grow Your Electronic Business with ZOBOX</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Get access to guaranteed inventory, zero repair hassles, priority logistics, and dedicated account managers.
                  </p>
                  <div className="pt-2">
                    <span className="px-4 py-2 rounded-lg bg-[#2B3380] text-white text-xs font-bold inline-block">
                      Apply for Partnership →
                    </span>
                  </div>
                </div>
                <div className="w-48 flex justify-center">
                  <img src={resolveMediaUrl('/assets/Zobiz.svg')} alt="Zobiz" className="max-h-36 object-contain" onError={handleImageError} />
                </div>
              </div>
            </div>
          )}

          {/* Specialized Section: Buy Mobile & Buy Laptop Catalog Previews */}
          {['buy-mobile', 'buy-laptop'].includes(form.slug) && (
            <div className="border-t pt-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#00b4d8]">
                  CATALOG PREVIEW
                </span>
                <span className="text-[11px] font-semibold text-[#2B3380]">All Devices Certified</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                {[
                  { name: form.slug === 'buy-laptop' ? 'MacBook Air M1' : 'iPhone 13 128GB', grade: 'Superb', price: '₹34,999', orig: '₹69,900' },
                  { name: form.slug === 'buy-laptop' ? 'Dell Latitude 7420' : 'Samsung S22 5G', grade: 'Excellent', price: '₹28,499', orig: '₹54,999' },
                  { name: form.slug === 'buy-laptop' ? 'Lenovo ThinkPad T14' : 'OnePlus 10R 5G', grade: 'Good', price: '₹20,699', orig: '₹39,998' },
                  { name: form.slug === 'buy-laptop' ? 'HP EliteBook 840' : 'Redmi Note 12 Pro', grade: 'Superb', price: '₹14,299', orig: '₹24,999' },
                ].map((dev) => (
                  <div key={dev.name} className="border rounded-xl p-3 bg-white space-y-1.5 shadow-sm">
                    <div className="h-20 bg-slate-50 rounded-lg flex items-center justify-center">
                      {form.slug === 'buy-laptop' ? <Laptop className="w-8 h-8 text-slate-400" /> : <Smartphone className="w-8 h-8 text-slate-400" />}
                    </div>
                    <strong className="text-gray-900 block truncate font-medium">{dev.name}</strong>
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-slate-900">{dev.price}</span>
                      <span className="text-slate-400 line-through text-[10px]">{dev.orig}</span>
                    </div>
                    <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold text-[9px]">
                      {dev.grade} Grade
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Specialized Section: FAQ Accordion Preview */}
          {form.slug === 'faq' && (
            <div className="border-t pt-6 space-y-3">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#00b4d8] block">
                FREQUENTLY ASKED QUESTIONS
              </span>
              {[
                { q: 'What warranty is offered on refurbished devices?', a: 'All devices come with a standard 6 to 12-month doorstep warranty.' },
                { q: 'How long does shipping take across India?', a: 'Orders are dispatched within 24 hours and typically delivered in 3-4 working days.' },
                { q: 'What is your return policy?', a: 'We offer an easy 7-day return and refund policy for verified functional defects.' },
              ].map((faq, idx) => (
                <div key={idx} className="border rounded-xl p-3 bg-white space-y-1 text-xs shadow-sm">
                  <div className="font-bold text-gray-900 flex items-center justify-between">
                    <span>{faq.q}</span>
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-[11px] pt-1 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          )}

          {/* Generic Page Cards Grid if pageItems exist */}
          {pageItems.length > 0 && !['wholesaler', 'refurbished', 'press-release', 'help', 'grade-condition', 'offers', 'buy-mobile', 'buy-laptop', 'faq'].includes(form.slug) && (
            <div className="border-t pt-6 space-y-4">
              <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                <Layers className="w-3.5 h-3.5 text-indigo-700" />
                <span>Page Sections &amp; Items ({pageItems.length})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pageItems.map((item, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-xl p-4 bg-slate-50/70 hover:bg-slate-50 transition flex flex-col justify-between">
                    <div>
                      {item.image && (
                        <img
                          src={resolveMediaUrl(item.image)}
                          alt={item.title || ''}
                          className="w-full h-36 object-contain bg-white rounded-lg mb-3 border p-2"
                          onError={handleImageError}
                        />
                      )}
                      {item.eyebrow && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#2B3380] block mb-1">
                          {item.eyebrow}
                        </span>
                      )}
                      <h4 className="font-bold text-gray-900 text-sm">{item.title}</h4>
                      {item.description && (
                        <p className="text-xs text-gray-600 mt-1 leading-relaxed">{item.description}</p>
                      )}
                    </div>
                    {(item.cta_label || item.href) && (
                      <div className="mt-3 pt-2 border-t border-slate-200">
                        <span className="text-xs font-semibold text-[#2B3380] hover:underline flex items-center gap-1">
                          {item.cta_label || 'Explore'} →
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(form.cta_text || form.cta_url) && (
            <div className="border-t pt-6">
              <span className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-[#2B3380] text-white text-sm font-semibold shadow-sm">
                {form.cta_text || 'Action Button'}
                <ExternalLink className="w-4 h-4 ml-1" />
              </span>
            </div>
          )}
        </div>
      )}

      {/* 4. SEO Preview Footer Snippet */}
      <div className="bg-slate-50 border-t p-4 text-xs text-slate-500">
        <span className="font-bold text-slate-700 block mb-1">Search Engine Snippet Preview:</span>
        <div className="font-medium text-blue-700 text-sm truncate">
          {form.seo_title || form.title || 'ZOBOX Page'}
        </div>
        <div className="text-emerald-700 text-[11px] truncate">
          https://zobox.in{route}
        </div>
        <div className="text-slate-600 mt-0.5 line-clamp-2">
          {form.seo_description || form.excerpt || 'Discover refurbished phones, laptops and technology on ZOBOX.'}
        </div>
      </div>
    </div>
  );
}
